import React, { useState, useCallback } from 'react';
import { ROLES, SxStyle } from '../../../types';
import Grid2 from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PreviewPushDevice from './components/preview-push-device';
import { useStringsContext } from '../../../contexts/strings';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useRouteMatch } from 'react-router-dom';
import PushNotificationTargetGroup from './components/category/push-notification-target';
import PushNotificationContent from './components/category/push-notification-content';
import PushNotificationSchedule from './components/category/push-notification-schedule';
import { useFormValidate } from '../../../hooks/formValidate';
import PushNotificationDestination from './components/category/push-notification-destination';
import {
  SendPushdNotificationBody,
  PushNotification,
  SchedulePushdNotificationBody,
} from '../../../types/notification';
import { useAppConfig } from '../../../hooks/appConfigs';
import {
  usePushNotificationSchedule,
  usePushNotificationSend,
} from '../../../hooks/pushNotification';
import useTeamData from '../../../hooks/useTeamData';
import { useUserAuth } from '../../../contexts/auth';

export type PushNotificationValues = Partial<PushNotification> &
  Pick<SchedulePushdNotificationBody, 'scheduledTime'>;

export enum PushSendType {
  Now = 'now',
  Scheduled = 'scheduled',
}

export const styles: SxStyle = {
  scheduleContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '500px',
  },
  publishButton: {
    marginTop: '2vh',
    marginBottom: '2vh',
    alignSelf: 'flex-end',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
};

const PushNoticationSend = () => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { params } = useRouteMatch<{ abbrv?: string }>();
  const selectedAbbrv = params.abbrv as string;

  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const isReadOnly = !isSuperAdmin && hasTeamRole(selectedAbbrv, ROLES.VIEWER);

  const { query: teamDataQuery } = useTeamData({ teamAbbrv: selectedAbbrv });
  const teamConfig = teamDataQuery?.data;

  const { query: appConfigQuery } = useAppConfig(selectedAbbrv);
  const appConfig = appConfigQuery?.data;

  const [allTargetFieldsValid, setAllTargetFieldsValid] = useState(false);
  const [allDestinationFieldsValid, setAllDestinationFieldsValid] = useState(false);
  const [allContentFieldsValid, setAllContentFieldsValid] = useState(false);

  const [hideValidationError, setHideValidationError] = useState(true);
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const [previewTitle, setPreviewTitle] = useState<string>();
  const [previewMessage, setPreviewMessage] = useState<string>();
  const [previewDate, setPreviewDate] = useState<string>();
  const [previewImage, setPreviewImage] = useState<string>();

  const [selectedSendValue, setSelectedSendValue] = useState<PushSendType>(PushSendType.Now);

  const defaultPushNotification: PushNotificationValues = {
    title: '',
    description: '',
    image: '',
    type: 'MATCH',
    target: {
      type: '' as 'ALL',
      value: '',
    },
  };

  const {
    addFormValue,
    addFormValidation,
    isValid,
    validatedValues,
    isDirty,
    editedValues,
    removeFormValue,
  } = useFormValidate<PushNotificationValues>();

  const { mutateAsync: sendPushNotificationMutation } = usePushNotificationSend(selectedAbbrv);
  const { mutateAsync: schedulePushNotificationMutation } =
    usePushNotificationSchedule(selectedAbbrv);

  const onSubmit = useCallback(
    async (event: { preventDefault: () => void }) => {
      event.preventDefault();

      if (!isValid) {
        setHideValidationError(false);
        return;
      }

      setSubmitInProgress(true);

      const pushNotificationBody: Partial<PushNotification> = {
        title: editedValues.title,
        description: editedValues.description,
        image: editedValues.image || '',
        target: editedValues.target,
        type: editedValues.type || 'WEB_PAGE',
        ...(editedValues.scene ? { scene: editedValues.scene } : {}),
        ...(editedValues.webPageUrl ? { webPageUrl: editedValues.webPageUrl } : {}),
        ...(editedValues.matchId ? { matchId: editedValues.matchId } : {}),
        ...(editedValues.pollId ? { pollId: editedValues.pollId } : {}),
        ...(editedValues.leagueName ? { leagueName: editedValues.leagueName } : {}),
        ...(editedValues.season ? { season: String(editedValues.season) } : {}),
      };

      if (selectedSendValue === PushSendType.Now) {
        try {
          await sendPushNotificationMutation({
            body: pushNotificationBody as SendPushdNotificationBody,
          });

          enqueueSnackbar(strings.messages.sendPushNotification, {
            variant: 'success',
          });
        } catch (error) {
          console.error(error);
          enqueueSnackbar(strings.messages.error, {
            variant: 'error',
          });
        }
      } else {
        try {
          if (!editedValues.scheduledTime) {
            return null;
          }
          await schedulePushNotificationMutation({
            body: pushNotificationBody as PushNotification,
            scheduledTime: editedValues.scheduledTime,
            app: selectedAbbrv,
          });

          enqueueSnackbar(strings.messages.scheduledPushNotification, {
            variant: 'success',
          });
        } catch (error) {
          console.error(error);
          enqueueSnackbar(strings.messages.error, {
            variant: 'error',
          });
        }
      }
      setSubmitInProgress(false);
    },
    [
      isValid,
      editedValues.title,
      editedValues.description,
      editedValues.image,
      editedValues.target,
      editedValues.type,
      editedValues.webPageUrl,
      editedValues.matchId,
      editedValues.pollId,
      editedValues.leagueName,
      editedValues.season,
      editedValues.scheduledTime,
      editedValues.scene,
      selectedSendValue,
      sendPushNotificationMutation,
      enqueueSnackbar,
      strings,
      schedulePushNotificationMutation,
      selectedAbbrv,
    ]
  );

  const onValue = useCallback(
    (key: string, value: string | boolean | null) => {
      addFormValue(key, value);

      if (key === 'title') {
        setPreviewTitle(value as string);
      } else if (key === 'description') {
        setPreviewMessage(value as string);
      } else if (key === 'scheduledTime') {
        setPreviewDate(value as string);
      } else if (key === 'image') {
        setPreviewImage(value as string);
      }
    },
    [addFormValue]
  );

  const onValidation = useCallback(
    (key: string, isValid: boolean) => {
      addFormValidation(key, isValid);
      setAllTargetFieldsValid(validatedValues.target);
      setAllDestinationFieldsValid(validatedValues.destination);
      setAllContentFieldsValid(validatedValues.title && validatedValues.description);
    },
    [
      addFormValidation,
      validatedValues.destination,
      validatedValues.description,
      validatedValues.target,
      validatedValues.title,
    ]
  );

  const onRemoveValue = useCallback(
    (key: string) => {
      removeFormValue(key);
    },
    [removeFormValue]
  );

  if (!appConfig || appConfigQuery.isLoading || appConfigQuery.isError) {
    return null;
  }

  return (
    <Grid2 container spacing={3}>
      <Grid2 width={500}>
        <Box sx={styles.scheduleContainer}>
          <PushNotificationTargetGroup
            selectedAbbrv={selectedAbbrv}
            pushNotification={defaultPushNotification}
            onValue={onValue}
            onValidation={onValidation}
            hideValidationError={hideValidationError}
            isReadOnly={isReadOnly}
          />
          <PushNotificationDestination
            expanded={allTargetFieldsValid}
            selectedAbbrv={selectedAbbrv}
            pushNotification={defaultPushNotification}
            onValue={onValue}
            onValidation={onValidation}
            onRemoveValue={onRemoveValue}
            hideValidationError={hideValidationError}
          />
          <PushNotificationContent
            expanded={allDestinationFieldsValid}
            pushNotification={defaultPushNotification}
            onValue={onValue}
            onValidation={onValidation}
            hideValidationError={hideValidationError}
          />
          <PushNotificationSchedule
            expanded={allContentFieldsValid}
            pushNotification={defaultPushNotification}
            onSendSelection={setSelectedSendValue}
            onValue={onValue}
            onValidation={onValidation}
            onRemoveValue={onRemoveValue}
            hideValidationError={hideValidationError}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            startIcon={<CloudUploadIcon />}
            sx={styles.publishButton}
            loading={submitInProgress}
            disabled={!isDirty}
          >
            {selectedSendValue === PushSendType.Now
              ? strings.pushNotifications.sendPushNow
              : strings.pushNotifications.sendPushLater}
          </Button>
        </Box>
      </Grid2>
      <Grid2 size="auto">
        <PreviewPushDevice
          abbrv={selectedAbbrv}
          name={appConfig.name}
          title={previewTitle}
          message={previewMessage}
          date={previewDate}
          image={previewImage}
          logo={teamConfig?.logoImageUrl}
        />
      </Grid2>
    </Grid2>
  );
};

PushNoticationSend.propTypes = {};

export default PushNoticationSend;
